import { API_URL } from "@/config";
import axios from "axios";
import { IPage } from "./api";

const API = `${API_URL}/settings`;
// Store-scoped pricing lives under the store router, not /settings.
const STORE_API = `${API_URL}/store`;

interface ISettings {
    id: number;
}

export interface IItemSizePricing extends ISettings {
    size_name: string;
    base_price: number;
}

export interface IDeliveryWindowPricing extends ISettings {
    window_type: string;
    additional_cost: number;
}

export interface IFloorLevelPricing extends ISettings {
    level: string;
    cost: number;
}

export interface IMiscCostPricing extends ISettings {
    cost_name: string;
    cost_value: number;
}

export interface IPricing extends ISettings {
    item_sizes: IItemSizePricing[];
    delivery_windows: IDeliveryWindowPricing[];
    floor_levels: IFloorLevelPricing[];
    misc_costs: IMiscCostPricing[];
}

export interface IItemCategory extends ISettings {
    category_name: string;
    parent_id: number | null;
    children: IItemCategory[];
}

export interface IItemCategoryUpdate extends ISettings {
    category_name: string;
    parent_id?: number | null;
}

export interface IItemCategoryCreate {
    category_name: string;
    parent_id: number | null;
}

export interface IDiscountCode extends ISettings {
    code: string;
    discount: number;
}

export interface IDiscountCodeCreate {
    code: string;
    discount: number;
}

export interface IDiscountCodeUpdate extends ISettings {
    code?: string;
    discount?: number;
}
export interface IHighValueUpcharge extends ISettings {
    minimum_value: number;
    upcharge: number;
}

export interface IHighValueUpchargeCreate {
    minimum_value: number;
    upcharge: number;
}

export interface IHighValueUpchargeUpdate extends ISettings {
    minimum_value?: number;
    upcharge?: number;
}

export interface IDistanceSurcharge extends ISettings {
    minimum_miles: number;
    surcharge: number;
}

export interface IDistanceSurchargeCreate {
    minimum_miles: number;
    surcharge: number;
}

export interface IDistanceSurchargeUpdate extends ISettings {
    minimum_miles?: number;
    surcharge?: number;
}

export const getPricing = async () => {
    const result: { data: IPricing } = await axios.get(`${API}/pricing`);
    return result.data;
};

export const updateItemSize = async (itemSize: IItemSizePricing) => {
    const result: { data: IItemSizePricing } = await axios.put(
        `${API}/item-size/${itemSize.id}`,
        itemSize
    );
    return result.data;
};

export const updateDeliveryWindow = async (
    deliveryWindow: IDeliveryWindowPricing
) => {
    const result: { data: IDeliveryWindowPricing } = await axios.put(
        `${API}/delivery-window/${deliveryWindow.id}`,
        deliveryWindow
    );
    return result.data;
};

export const updateFloorLevel = async (floorLevel: IFloorLevelPricing) => {
    const result: { data: IFloorLevelPricing } = await axios.put(
        `${API}/floor-level/${floorLevel.id}`,
        floorLevel
    );
    return result.data;
};

export const updateMiscCost = async (miscCost: IMiscCostPricing) => {
    const result: { data: IMiscCostPricing } = await axios.put(
        `${API}/misc-cost/${miscCost.id}`,
        miscCost
    );
    return result.data;
};

export const getItemCategories = async () => {
    const result: { data: IPage<IItemCategory> } = await axios.get(
        `${API}/item-category`
    );
    return result.data;
};

export const createItemCategory = async (itemCategory: IItemCategoryCreate) => {
    const result: { data: IItemCategory } = await axios.post(
        `${API}/item-category`,
        itemCategory
    );
    return result.data;
};

export const updateItemCategory = async (itemCategory: IItemCategoryUpdate) => {
    const result: { data: IItemCategory } = await axios.put(
        `${API}/item-category/${itemCategory.id}`,
        itemCategory
    );
    return result.data;
};

export const deleteItemCategory = async (id: number) => {
    await axios.delete(`${API}/item-category/${id}`);
};

export const getDiscountCodes = async () => {
    const result = await axios.get<IPage<IDiscountCode>>(
        `${API}/discount-codes`
    );

    return result.data;
};

export const createDiscountCode = async (discount: IDiscountCodeCreate) => {
    const result = await axios.post<IDiscountCode>(
        `${API}/discount-codes`,
        discount
    );

    return result.data;
};

export const updateDiscountCode = async (discount: IDiscountCodeUpdate) => {
    const result = await axios.put<IDiscountCode>(
        `${API}/discount-codes/${discount.id}`,
        discount
    );

    return result.data;
};

export const deleteDiscountCode = async (id: number) => {
    const result = await axios.delete<IDiscountCode>(
        `${API}/discount-codes/${id}`
    );
    return result.data;
};

export const getHighValues = async () => {
    const result = await axios.get<IPage<IHighValueUpcharge>>(
        `${API}/high-value`
    );

    return result.data;
};

export const createHighValue = async (highValue: IHighValueUpchargeCreate) => {
    const result = await axios.post<IHighValueUpcharge>(
        `${API}/high-value`,
        highValue
    );

    return result.data;
};

export const updateHighValue = async (highValue: IHighValueUpchargeUpdate) => {
    const result = await axios.put<IDiscountCode>(
        `${API}/high-value/${highValue.id}`,
        highValue
    );

    return result.data;
};

export const deleteHighValue = async (id: number) => {
    const result = await axios.delete<IHighValueUpcharge>(
        `${API}/high-value/${id}`
    );
    return result.data;
};

export const getDistanceSurcharges = async () => {
    const result = await axios.get<IPage<IDistanceSurcharge>>(
        `${API}/distance-surcharge`
    );

    return result.data;
};

export const createDistanceSurcharge = async (
    distanceSurcharge: IDistanceSurchargeCreate
) => {
    const result = await axios.post<IDistanceSurcharge>(
        `${API}/distance-surcharge`,
        distanceSurcharge
    );

    return result.data;
};

export const updateDistanceSurcharge = async (
    distanceSurcharge: IDistanceSurchargeUpdate
) => {
    const result = await axios.put<IDistanceSurcharge>(
        `${API}/distance-surcharge/${distanceSurcharge.id}`,
        distanceSurcharge
    );

    return result.data;
};

export const deleteDistanceSurcharge = async (id: number) => {
    const result = await axios.delete<IDistanceSurcharge>(
        `${API}/distance-surcharge/${id}`
    );
    return result.data;
};

// --- store-scoped pricing -------------------------------------------------
//
// Global pricing is administered through the helpers above, unchanged. These target
// the store-scoped surface, which cannot write a global row.

export interface IStorePricingOverride {
    id: number;
    key: string;
    value: number;
    /** True when this row REMOVES a global ladder tier for the store, rather than
     *  overriding its amount. The two render differently and must not be conflated. */
    suppressed: boolean;
}

export interface IStorePricingOverrides {
    /** Echoed back so the UI can discard a response from a previously selected store. */
    store_id: number;
    groups: Record<string, IStorePricingOverride[]>;
}

export type PricingGroupSlug =
    | "item-size"
    | "floor-level"
    | "delivery-window"
    | "misc-cost"
    | "high-value"
    | "distance-surcharge";

/**
 * Effective pricing, optionally resolved for a store.
 *
 * Passing a store returns its overrides merged over global, in the same response shape
 * as the global read — which is why nothing downstream needs to change.
 */
export const getPricingForScope = async (storeId?: number | null) => {
    const result: { data: IPricing } = await axios.get(`${API}/pricing`, {
        params: storeId != null ? { store_id: storeId } : undefined,
    });
    return result.data;
};

/** A store's OWN rows — what the merged sheet cannot tell you. Superuser only. */
export const getStorePricingOverrides = async (storeId: number) => {
    const result: { data: IStorePricingOverrides } = await axios.get(
        `${STORE_API}/${storeId}/pricing-overrides`
    );
    return result.data;
};

/** Idempotent by (store, key), so the caller never chooses between create and update. */
export const upsertStorePricingOverride = async (
    storeId: number,
    group: PricingGroupSlug,
    key: string,
    value: number
) => {
    const result: { data: IStorePricingOverride } = await axios.put(
        `${STORE_API}/${storeId}/pricing/${group}`,
        { key, value }
    );
    return result.data;
};

/**
 * Remove a value for this store.
 *
 * For a keyed group this reverts the override so the key inherits global again. For a
 * ladder tier that exists only globally it records a suppression instead — deleting the
 * global row would remove that tier for every store.
 */
export const removeStorePricingOverride = async (
    storeId: number,
    group: PricingGroupSlug,
    key: string
) => {
    // The key MUST be encoded: it goes in the path, and real keys contain characters
    // that are not path-safe. Delivery windows are "7 Days", "5 Days" and so on -- a
    // raw space produces a malformed URL whose handling varies by client -- and the
    // top floor bracket is "3+". Starlette percent-decodes path parameters, so the
    // handler still receives the original key.
    await axios.delete(
        `${STORE_API}/${storeId}/pricing/${group}/${encodeURIComponent(key)}`
    );
};
